import { useCallback, useEffect, useState } from "react";
import { useDiagnosticNavigation } from "../useDiagnosticNavigation";
import { usePatientCase } from "../usePatientCase";
import { useCreateSharedCase } from "../hooks/useCreateSharedCase";
import { UserManagementPopupMode } from "../../../context/diagnosticAgent";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import { isValidSession } from "user-management/src/cognito/kahunUserAuth";
import { saveCase } from "../../../saveCase/savedCases";

export type UseSaveCaseApi = {
  saveCase: () => void;
  displaySuccess: boolean;
  loading: boolean;
};

//region [[ Text ]]
const TOAST_TEXT = "The case was added to 'My Cases' under main menu";
const LOGIN_FIRST_MESSAGE = "Please sign in to be able to save the case";
//endregion [[ Text ]]

export const useSaveCase = (): UseSaveCaseApi => {
  const [waitingForLogin, setWaitingForLogin] = useState(false);

  const [isUserLoggedIn, setIsUserLoggedIn] = useState(false);

  const [loading, setLoading] = useState(false);

  const [displaySuccess, setDisplaySuccess] = useState(false);

  const { setToastOpen, setToastText } = useDiagnosticNavigation();

  const {
    setLoginPopupState,
    loginPopupState,
    setLoginPopupMessage,
  } = useDiagnosticNavigation();

  const { patientCase } = usePatientCase();

  const createSharedCase = useCreateSharedCase();

  const onSaveCaseRequest = useCallback(() => {
    //If user isn't logged in prompt log-in first
    if (!isUserLoggedIn) {
      setWaitingForLogin(true);
      setLoginPopupState(UserManagementPopupMode.Login);
      setLoginPopupMessage(LOGIN_FIRST_MESSAGE);
      return;
    }

    setWaitingForLogin(false);
    setLoading(true);

    //Save the case
    (async () => {
      const patientCaseId = await createSharedCase();
      if (patientCaseId) {
        await saveCase(patientCase, patientCaseId);
        setDisplaySuccess(true);
        setLoading(false);
        evt({
          category: CATEGORY.AGENT,
          action: ACTION.SAVE_CASE,
          label: patientCaseId,
        });
        // Change back to regular icon
        setTimeout(() => setDisplaySuccess(false), 5000);
        setToastText(TOAST_TEXT);
        setToastOpen(true);
      }
    })();
  }, [
    createSharedCase,
    isUserLoggedIn,
    patientCase,
    setLoginPopupState,
    setLoginPopupMessage,
    setToastText,
    setToastOpen,
  ]);

  // If popup was closed, check for login
  useEffect(() => {
    if (loginPopupState === UserManagementPopupMode.Closed) {
      (async () => {
        const isValid = await isValidSession();
        setIsUserLoggedIn(isValid);
      })();
    }
  }, [loginPopupState, setIsUserLoggedIn]);

  // After state changed to logged in, if component was waiting for log-in save the case
  useEffect(() => {
    if (waitingForLogin && isUserLoggedIn) {
      // If was waiting for login and login succeeded save the case
      onSaveCaseRequest();
    }
  }, [isUserLoggedIn, waitingForLogin, onSaveCaseRequest]);

  return { saveCase: onSaveCaseRequest, displaySuccess, loading };
};
